import React, { useEffect } from 'react'
import { Typography, Container, Button } from '@/src/ct-design/lib'
import { DESKTOP } from '@/src/constants'
import { DeviceType } from '@/src/types'
import Modal from '@/src/ct-design/lib/Modal'
import { ERROR_MESSAGES } from '@/src/constants/error'
import { redirectToVisaSignIn, navigateToVisaHome } from '@/src/utils/helper'

interface ErrorModalProps {
    isOpen: boolean
    onClose: () => void
    error: {
        code: string
        message: string
    }
    deviceType: DeviceType
}

const ErrorModal: React.FC<ErrorModalProps> = ({
    isOpen,
    onClose,
    error,
    deviceType
}) => {
    const isDesktop = deviceType === DESKTOP

    // Auto-redirect after 5 seconds for better UX
    useEffect(() => {
        if (isOpen) {
            const redirectTimer = setTimeout(() => {
                navigateToVisaHome()
                onClose()
            }, 5000)

            return () => clearTimeout(redirectTimer)
        }
    }, [isOpen, onClose])

    const handleGoHome = () => {
        navigateToVisaHome()
        onClose()
    }

    // Determine if we should show a specific action button
    const determineActionButton = () => {
        // Network errors should prompt users to check connection
        if (error.code === 'NETWORK_ERROR') {
            return (
                <Button
                    color='secondary'
                    size={isDesktop ? 'medium' : 'small'}
                    onClick={() => window.location.reload()}
                >
                    Refresh Page
                </Button>
            )
        }

        // For authentication issues, offer sign in
        if (error.code === 'HTTP_401' || error.code === 'HTTP_403') {
            return (
                <Button
                    color='secondary'
                    size={isDesktop ? 'medium' : 'small'}
                    onClick={() => {
                        redirectToVisaSignIn()
                    }}
                >
                    Sign In
                </Button>
            )
        }

        // Default go home button
        return (
            <Button
                color='secondary'
                size={isDesktop ? 'medium' : 'small'}
                onClick={handleGoHome}
            >
                Go to Home
            </Button>
        )
    }

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            size={isDesktop ? 'MEDIUM' : 'SMALL'}
            hideCrossIcon={false}
        >
            <Container
                className={`flex flex-column flex-middle p-8 ${isDesktop ? 'px-10' : 'px-4'}`}
            >
                <Typography
                    variant={isDesktop ? 'HD3' : 'HM2'}
                    color='warning'
                    className='mb-4 ta-c'
                >
                    Something went wrong
                </Typography>

                <Container className='p-4 bg-error-100 mb-6 br-6 w-100p'>
                    <Typography
                        variant={isDesktop ? 'P1' : 'P2'}
                        className='ta-c'
                    >
                        {error.message}
                    </Typography>
                </Container>

                <Typography
                    variant={isDesktop ? 'P2' : 'P3'}
                    className='mb-6 ta-c'
                >
                    You'll be redirected to the homepage in a few seconds, or
                    you can click the button below.
                </Typography>

                {determineActionButton()}
            </Container>
        </Modal>
    )
}

export default ErrorModal
